// The editor's page: /edit/#<page>. A page is named by what follows the #,
// which no request carries, so opening one tells the site nothing.
// /edit/#file:docs/how-it-works.md opens one of the site's own files instead, and
// mountEditor() puts the editor on any file a host can read and write.
import init from "loro-crdt/web/loro_wasm.js";
import { render, type ComponentChildren } from "preact";
import { memo } from "preact/compat";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { Editor } from "./editor";
import { HostContext, MadeContext, PageView, defsOf, type Host } from "./render";
import { allowStyle, css, PROPERTIES } from "./style";
import { componentCss, components } from "./components";
import { Page, HIDDEN, type NodeJSON, type TreeID } from "./doc";
import { Library, Panel, inspect, type Tab } from "./panel";
import { print, parse } from "./textform";
import { fileOf, pageFrom, readFile, targetOf } from "./history";
import { publish, rulesOf } from "./publish";
import { applyTool, Suggestion, TOOLS } from "./ai";
import { save } from "./store";
import { fixtures } from "./fixture";
import { FileSession, type FileHost, type Room } from "./file";
import { keepIds, parseMarkdown, printMarkdown } from "./markdown";
// The code editor (CodeMirror) is a chunk of its own, fetched only by a
// machine's file: the editor's own page never needs it.
let code: typeof import("./code") | undefined;
const loadCode = async () => (code ??= await import("./code"));
import type { EditorView } from "@codemirror/view";
import {
  Alert, AlertDescription, Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  Textarea, Toaster, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, toast,
} from "../ui";

// A control's name for the pointer, shown on hover and focus.
function Tip({ tip, children }: { tip: string; children: ComponentChildren }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}
const Dots = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>
);

declare global {
  interface Window {
    editor?: Editor; // for tests, and the curious
    editorLib?: Record<string, unknown>;
    editorFile?: FileSession;
    editorView?: EditorView; // a machine's file's CodeMirror, for tests
  }
}
window.editorLib = { Page, allowStyle, css, PROPERTIES, components, print, parse, inspect, applyTool, Suggestion, TOOLS, publish, fixtures, pageFrom, readFile, fileOf, targetOf, renderTo, parseMarkdown, printMarkdown, keepIds, mountEditor, mountViewer };

// Draw nodes into an element with no editor around them, as a published
// page is; for tests that fuzz the renderer.
function renderTo(el: HTMLElement, nodes: NodeJSON[], defs: NodeJSON[] = []) {
  render(
    <MadeContext.Provider value={{ defs: defsOf(defs), within: [] }}>
      <PageView nodes={nodes} />
    </MadeContext.Provider>,
    el,
  );
}

// A file of the site's own: docs/… or spec/…, as served beside the editor.
function filePath(): string | undefined {
  const h = decodeURIComponent(location.hash.slice(1));
  const m = h.match(/^file:((?:docs|spec)\/[a-z0-9-]+\.md)$/);
  return m?.[1];
}

function pageId(): string {
  const h = decodeURIComponent(location.hash.slice(1));
  if (/^[a-z0-9-]{4,64}$/i.test(h)) return h;
  const id = newId();
  history.replaceState(null, "", `#${id}`);
  return id;
}
const newId = () => Array.from(crypto.getRandomValues(new Uint8Array(6)), (b) => "abcdefghijkmnpqrstuvwxyz23456789"[b % 32]).join("");

// The page's styles: one constructed stylesheet for the components' own,
// and one rewritten when a style or token changes. Nothing is ever written
// to a style attribute.
const base = new CSSStyleSheet();
base.replaceSync(componentCss(".page"));
const sheet = new CSSStyleSheet();
document.adoptedStyleSheets = [...document.adoptedStyleSheets, base, sheet];
function restyle(nodes: NodeJSON[], tokens: Record<string, string>) {
  sheet.replaceSync(css(rulesOf(nodes), tokens));
}

function download(name: string, text: string, type: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type }));
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

// Open a page from a file: its history merges into the page of the same
// name in this browser, or becomes a new one.
async function openFile(editor: Editor, file: File) {
  const opened = readFile(await file.text());
  const id = opened.id ?? newId();
  if (id === editor.id && opened.snapshot) return editor.merge(opened.snapshot);
  await save(id, pageFrom(opened).doc);
  location.hash = id;
}

// The site's own files, served beside the editor. The site can't be
// written, so a save here is kept in this tab, and Save offers the file as
// a download; tests write `disk` to stand for another program's change.
class SiteHost implements FileHost {
  disk?: { text: string; version: string };
  constructor(readonly path: string) {}
  async read() {
    if (!this.disk) {
      const r = await fetch(`src/${this.path}`);
      if (!r.ok) throw new Error(`there is no ${this.path}`);
      const text = await r.text();
      this.disk = { text, version: await versionOf(text) };
    }
    return this.disk;
  }
  async write(text: string, base: string) {
    if (base !== this.disk?.version) return { changed: true } as const;
    this.disk = { text, version: await versionOf(text) };
    return { version: this.disk.version };
  }
}
export async function versionOf(text: string): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(d).slice(0, 8), (b) => b.toString(16).padStart(2, "0")).join("");
}

// Rendered editing is off in a machine's file for now: raw source, edited
// together, comes first (the human's call, 2026-10-25). The page view, its
// panel and its slash menu stay for the editor's own page, and come back
// here by setting this. meets: files/raw-source
const RAW = true;

let loro: Promise<unknown> | undefined;
const ready = () => (loro ??= init({ module_or_path: new URL("loro.wasm", import.meta.url) }));

// The editor on a file a host reads and writes, drawn into `el`; the page
// also needs app.css. `autosave`: milliseconds after an edit to save.
// A file that isn't markdown is its text alone (`plain`), and opens in the
// code editor; markdown opens in the same editor, set as a document.
export async function mountEditor(el: HTMLElement, host: FileHost, opts: { autosave?: number; room?: Room; plain?: boolean } = {}) {
  await Promise.all([ready(), loadCode()]);
  const plain = opts.plain ?? !/\.(md|markdown)$/i.test(host.path);
  const file = await FileSession.open(host, { autosave: opts.autosave, room: opts.room, plain });
  window.editor = file.editor;
  window.editorFile = file;
  el.replaceChildren(); // "Opening the file…"
  render(<App editor={file.editor} file={file} embedded />, el);
  return { editor: file.editor, file, close: () => (render(null, el), file.close()) };
}

type BarProps = {
  editor: Editor; file?: FileSession; embedded?: boolean; source: boolean; setSource: (v: boolean) => void; setWhy: (v?: string) => void;
  people: string; me: string; state?: string; stateWhy?: string;
};
const Bar = memo(function Bar({ editor, file, embedded, source, setSource, setWhy }: BarProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const others = editor.presence.others(editor.page.doc);
  const isSite = file?.host instanceof SiteHost;
  const save = async () => {
    if (!file) {
      download(`${editor.id}.page.md`, fileOf(editor.page, editor.id), "text/markdown");
      return toast("Saved as a file", { description: `${editor.id}.page.md, with its history` });
    }
    if (!(await file.save())) return;
    if (isSite) {
      download(file.host.path.split("/").pop()!, (file.host as SiteHost).disk!.text, "text/markdown");
      toast("Downloaded", { description: file.host.path });
    }
  };
  const publishPage = () => {
    const title = editor.page.text(editor.nodes.find((n) => n.type === "heading")?.id ?? ("" as TreeID))?.toString() || "Page";
    download(`${editor.id}.html`, publish(editor.nodes, editor.definitions, editor.page.tokens.toJSON() as Record<string, string>, title), "text/html");
    toast("Published", { description: `${editor.id}.html, one static file` });
  };
  const saveTip = file ? (isSite ? "Download the file as it is now" : "Save the file") : "Save this page, with its history, as a file";
  // A machine's file is saved by itself, a second after each edit, and is
  // only source for now (RAW): no Save, no Publish, no Page and Source.
  const live = !!file && !isSite;
  // A machine's file in the files pane says nothing while all is well:
  // the bar shows only others here, or a save that failed (design.md,
  // principles 2 and 5). Its state stays, unseen, for a screen reader.
  const quiet = live && embedded;
  return (
      <header class={`ed-bar${quiet ? " quiet" : ""}`}>
        <div class="ed-bar-inner">
          {!embedded && (
            <a class="ed-brand" href="/">
              <img src="/favicon.svg" alt="" width="20" height="20" />
              <span>editor</span>
            </a>
          )}
          {file ? (
            <span class="where">
              {/* The pane around a machine's file names it already. */}
              {!(live && embedded) && <span class="where-path"><code>{file.host.title ?? file.host.path}</code></span>}
              <Badge variant={file.state === "saved" ? "secondary" : file.state === "failed" ? "destructive" : "outline"} class={`file-state${quiet && file.state !== "failed" ? " sr" : ""}`} data-file-state={file.state} title={file.why}>{STATES[file.state]}</Badge>
            </span>
          ) : (
            <span class="where">
              <span class="where-label">Page</span> <code>{editor.id}</code>
            </span>
          )}
          <span class="people" aria-label="Who is here">
            {!quiet && (
              <span class={`person me ${hueClass(editor.presence.hue)}`} title="You">
                {editor.presence.name} (you)
              </span>
            )}
            {others.map((p) => (
              <span class={`person ${hueClass(p.hue)}`} data-peer={p.peer} key={p.peer}>
                {p.name}
              </span>
            ))}
          </span>
          <span class="actions">
            {!(live && RAW) && (
              <Tip tip={source ? "Back to the page" : "The page as text"}>
                <Button variant="outline" size="sm" aria-pressed={source} data-action="source" onClick={() => setSource(!source)}>
                  {source ? "Page" : "Source"}
                </Button>
              </Tip>
            )}
            {!file && (
              <Tip tip="Open a page from a file">
                <Button variant="outline" size="sm" class="wide-only" data-action="open" onClick={() => fileInput.current?.click()}>Open</Button>
              </Tip>
            )}
            {!live && (
              <Tip tip="This page as one static HTML file">
                <Button variant="outline" size="sm" class="wide-only" data-action="publish" onClick={publishPage}>Publish</Button>
              </Tip>
            )}
            {!live && (
              <Tip tip={saveTip}>
                <Button size="sm" data-action="save" onClick={save}>{isSite ? "Download" : "Save"}</Button>
              </Tip>
            )}
            <input
              ref={fileInput}
              type="file"
              accept=".md,.txt,text/markdown,text/plain"
              hidden
              data-action="open-file"
              onChange={async (e) => {
                const f = (e.target as HTMLInputElement).files?.[0];
                (e.target as HTMLInputElement).value = "";
                if (!f) return;
                try {
                  await openFile(editor, f);
                  setWhy(undefined);
                } catch (err) {
                  setWhy(`That file didn't open: ${(err as Error).message}`);
                }
              }}
            />
            {!(live && embedded) && <DropdownMenu>
              <Tip tip="More">
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon-sm" aria-label="More" data-more><Dots /></Button>
                </DropdownMenuTrigger>
              </Tip>
              <DropdownMenuContent align="end">
                {!file && <DropdownMenuItem class="narrow-only" onSelect={() => fileInput.current?.click()}>Open a file…</DropdownMenuItem>}
                {!live && <DropdownMenuItem class="narrow-only" onSelect={publishPage}>Publish</DropdownMenuItem>}
                {!embedded && (
                  <DropdownMenuItem asChild>
                    <a href={location.href} target="_blank" rel="noopener">Open in a new tab</a>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>}
          </span>
        </div>
      </header>
  );
});

function App({ editor, file, embedded }: { editor: Editor; file?: FileSession; embedded?: boolean }) {
  const [, setTick] = useState(0);
  const [tab, setTab] = useState<Tab>("style");
  const [picking, setPicking] = useState(false);
  const [source, setSource] = useState(false);
  const [why, setWhy] = useState<string>();
  useEffect(() => editor.subscribe(() => setTick((n) => n + 1)), [editor]);
  useEffect(() => file?.subscribe(() => setTick((n) => n + 1)), [file]);
  const nodes = editor.nodes;
  const defs = editor.definitions;
  useEffect(() => restyle([...nodes, ...defs], editor.page.tokens.toJSON() as Record<string, string>));
  const others = editor.presence.others(editor.page.doc);
  const host = useHost(editor);
  // The bar redraws only when what it shows changes, not on every keystroke.
  const people = others.map((p) => `${p.peer}:${p.name}:${p.hue}`).join("|");
  const raw = RAW && !!file && !(file.host instanceof SiteHost);
  return (
    <TooltipProvider>
      <Bar editor={editor} file={file} embedded={embedded} source={source} setSource={setSource} setWhy={setWhy}
        people={people} me={`${editor.presence.name}:${editor.presence.hue}`} state={file?.state} stateWhy={file?.why} />
      {(why || file?.why) && (
        <div class="bar-why">
          {why && <Alert variant="destructive" class="why"><AlertDescription>{why}</AlertDescription></Alert>}
          {file?.why && <Alert variant="destructive" class="why"><AlertDescription>{file.why}</AlertDescription></Alert>}
        </div>
      )}
      <main class={`desk ${hueClass(editor.presence.hue)}${raw ? " raw" : ""}`}>
        {raw ? (
          <CodeView editor={editor} path={file!.host.path} />
        ) : source ? (
          <SourceView editor={editor} />
        ) : (
          <article class={`page${picking ? " picking" : ""}`} data-page={editor.id} onClickCapture={(e) => {
            if (!picking) return;
            const el = (e.target as HTMLElement).closest?.("[data-node]") as HTMLElement | null;
            if (!el) return;
            e.preventDefault();
            e.stopPropagation();
            editor.select(el.dataset.node as TreeID);
          }}>
            <HostContext.Provider value={host}>
              <MadeContext.Provider value={{ defs: defsOf(defs), within: [] }}>
                <PageView nodes={nodes} />
              </MadeContext.Provider>
            </HostContext.Provider>
            <Cursors editor={editor} others={others} />
            {editor.slashed && <SlashMenu editor={editor} />}
          </article>
        )}
        {!raw && <Panel editor={editor} host={host} tab={tab} setTab={setTab} picking={picking} setPicking={setPicking} />}
      </main>
      <Toaster />
    </TooltipProvider>
  );
}

// The page's text form, printed as edits arrive, and edited in place: each
// pause in typing becomes the edits a person would have made.
function SourceView({ editor }: { editor: Editor }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const base = useRef(editor.source());
  const pending = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [why, setWhy] = useState<string>();
  const apply = () => {
    pending.current = undefined;
    const area = ref.current;
    if (!area) return;
    const problem = editor.editSource(base.current, area.value);
    setWhy(problem);
    if (!problem) refresh();
  };
  // Show the page as it is now, keeping the caret where it was in the text.
  const refresh = () => {
    const area = ref.current;
    if (!area || pending.current) return;
    const next = editor.source();
    base.current = next;
    if (area.value === next.text) return;
    const old = area.value;
    const caret = area.selectionStart;
    let p = 0;
    while (p < old.length && p < next.text.length && old[p] === next.text[p]) p++;
    area.value = next.text;
    const at = caret <= p ? caret : Math.max(p, caret + next.text.length - old.length);
    if (document.activeElement === area) area.setSelectionRange(at, at);
  };
  useEffect(() => editor.subscribe(refresh), [editor]);
  useLayoutEffect(() => {
    if (ref.current) ref.current.value = base.current.text;
    return () => pending.current && (clearTimeout(pending.current), apply());
  }, []);
  return (
    <section class="source">
      <p class="hint">{editor.markdown
        ? "The file, as it is saved: markdown, with no ids in it. Edit it, and the page follows."
        : "The page's text form: markdown for words, tags for components, an id on each block. Edit it, and the page follows."}</p>
      <Textarea
        ref={ref}
        class="source-text"
        aria-label="Source"
        spellcheck={false}
        onInput={() => {
          clearTimeout(pending.current);
          pending.current = setTimeout(apply, 300);
        }}
      />
      {why && <p class="why" role="alert">{why}</p>}
    </section>
  );
}

// A machine's file, as its text, in CodeMirror (code.ts): code coloured and
// numbered, markdown set as a document. Each pause in typing is diffed into
// the page, as the source view above does, and what the page becomes (the
// room's edits, the disk's) is drawn back as the one smallest change, so a
// caret stays where it was. meets: files/raw-source
function CodeView({ editor, path }: { editor: Editor; path: string }) {
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    let base = editor.source();
    let pending: ReturnType<typeof setTimeout> | undefined;
    const apply = () => {
      pending = undefined;
      editor.editSource(base, view.state.sliceDoc()); // in the file's own line ends
      refresh();
    };
    const refresh = () => {
      if (pending) return;
      base = editor.source();
      code!.setText(view, base.text);
    };
    const view = code!.codeEditor(ref.current!, {
      path,
      text: base.text,
      onEdit: () => {
        clearTimeout(pending);
        pending = setTimeout(apply, 60);
      },
    });
    window.editorView = view;
    const stop = editor.subscribe(refresh);
    return () => {
      stop();
      if (pending) clearTimeout(pending), apply();
      view.destroy();
    };
  }, [editor]);
  return <section class="source bare" ref={ref} />;
}

// A file too large to edit here, or not text: what of it can be shown,
// read only, with a note saying why. Needs app.css, as mountEditor does.
export async function mountViewer(el: HTMLElement, { path, text, note }: { path: string; text?: string; note: string }) {
  if (text !== undefined) await loadCode();
  el.replaceChildren();
  render(
    <main class="desk raw">
      <Alert class="file-note"><AlertDescription>{note}</AlertDescription></Alert>
      {text !== undefined && <section class="source bare" ref={(s) => { if (s && !s.firstChild) window.editorView = code!.codeEditor(s, { path, text, readOnly: true }); }} />}
    </main>,
    el,
  );
}

// `/` in an empty block: the library, to put a component there.
function SlashMenu({ editor }: { editor: Editor }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    const at = editor.slashed && document.querySelector(`.page [data-node="${editor.slashed}"]`);
    if (el && at) {
      const box = (el.offsetParent as HTMLElement).getBoundingClientRect();
      const r = at.getBoundingClientRect();
      el.style.translate = `${r.left - box.left}px ${r.bottom - box.top + 4}px`;
    }
    (el?.querySelector("button") as HTMLButtonElement | null)?.focus();
    const close = (e: KeyboardEvent) => e.key === "Escape" && ((editor.slashed = undefined), editor.changed());
    addEventListener("keydown", close);
    return () => removeEventListener("keydown", close);
  }, [editor.slashed]);
  // Up and down move through the library, as in any menu.
  const move = (e: KeyboardEvent) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const items = [...(ref.current?.querySelectorAll("button") ?? [])];
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    items[(at + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
    e.preventDefault();
  };
  return (
    <div class="slash-menu ui-menu" ref={ref} role="menu" aria-label="Insert a component" onKeyDown={move}>
      <Library editor={editor} />
    </div>
  );
}

// Hue is data from another tab; it becomes a class, never a style.
const hueClass = (h: number) => `hue-${Math.round(((h % 360) + 360) % 360 / 30) % 12}`;

function useHost(editor: Editor): Host {
  const ref = useRef<Host | undefined>(undefined);
  ref.current ??= {
    mountText: (id, el) => editor.mountText(id, el),
    block: (node, inner) => <Block editor={editor} node={node}>{inner}</Block>,
  };
  return ref.current;
}

// Every block has a handle: drag it to move the block, click it to select,
// shift-click to choose several.
// meets: move/keeps-edits
let dragging: TreeID | undefined;
function Block({ editor, node, children }: { editor: Editor; node: NodeJSON; children: ComponentChildren }) {
  const [drop, setDrop] = useState<"before" | "after" | undefined>();
  const others = editor.presence.others(editor.page.doc).filter((p) => p.node === node.id);
  const comments = editor.comments.list().filter((t) => t.node === node.id && !t.resolved && t.from === undefined);
  const place = (e: DragEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return e.clientY < r.top + r.height / 2 ? "before" : "after";
  };
  const picked = editor.picked.length > 1 && editor.picked.includes(node.id);
  return (
    <div
      class={`block${editor.selected === node.id ? " selected" : ""}${picked ? " picked" : ""}${drop ? ` drop-${drop}` : ""}${comments.length ? " commented" : ""}`}
      data-block={node.id}
      onDragOver={(e) => {
        if (!dragging || dragging === node.id) return;
        e.preventDefault();
        e.stopPropagation();
        setDrop(place(e));
      }}
      onDragLeave={() => setDrop(undefined)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDrop(undefined);
        const id = dragging;
        if (!id || id === node.id) return;
        const p = editor.page;
        const parent = p.parent(node.id);
        const siblings = p.children(parent).filter((c) => c !== id);
        const at = siblings.indexOf(node.id) + (place(e) === "after" ? 1 : 0);
        p.move(id, parent, at);
      }}
    >
      <button
        type="button"
        class="grip"
        draggable
        contentEditable={false}
        aria-label={`Move or select this ${node.type}`}
        title="Drag to move · click to select · shift-click to choose several"
        onDragStart={(e) => {
          dragging = node.id;
          e.dataTransfer?.setData("text/x-editor-node", node.id);
          if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
        }}
        onDragEnd={() => (dragging = undefined)}
        onClick={(e) => editor.select(e.shiftKey || editor.selected !== node.id ? node.id : undefined, e.shiftKey)}
      >
        ⠿
      </button>
      {others.map((p) => (
        <span class={`flag ${hueClass(p.hue)}`} key={p.peer} contentEditable={false}>
          {p.name}
        </span>
      ))}
      {comments.length > 0 && (
        <span class="badge" contentEditable={false} title={`${comments.length} comment${comments.length > 1 ? "s" : ""}`}>
          {comments.length}
        </span>
      )}
      {children}
    </div>
  );
}

// A drop on an empty container puts the block inside it.
addEventListener("dragover", (e) => {
  const into = (e.target as HTMLElement).closest?.("[data-into]");
  if (into && dragging) e.preventDefault();
});
addEventListener("drop", (e) => {
  const into = (e.target as HTMLElement).closest?.("[data-into]") as HTMLElement | null;
  if (!into || !dragging || !window.editor) return;
  e.preventDefault();
  window.editor.page.move(dragging, into.dataset.into as TreeID, 0);
});

// Everyone else's carets, drawn where their stable cursor is now.
function Cursors({ editor, others }: { editor: Editor; others: ReturnType<Editor["presence"]["others"]> }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const layer = ref.current;
    if (!layer) return;
    layer.replaceChildren();
    const box = layer.getBoundingClientRect();
    for (const p of others) {
      const v = p.node && editor.views.get(p.node);
      if (!v || p.to === undefined) continue;
      const size = v.view.state.doc.content.size;
      let c;
      try {
        c = v.view.coordsAtPos(Math.min(p.to, size));
      } catch {
        continue;
      }
      // Positions go through the CSSOM, which the CSP allows; there is no
      // style attribute anywhere.
      const caret = document.createElement("span");
      caret.className = `caret ${hueClass(p.hue)}`;
      caret.dataset.peer = p.peer;
      caret.dataset.at = String(p.to);
      caret.style.translate = `${c.left - box.left}px ${c.top - box.top}px`;
      caret.style.height = `${c.bottom - c.top}px`;
      const tag = document.createElement("span");
      tag.className = "caret-name";
      tag.textContent = p.name;
      caret.append(tag);
      layer.append(caret);
    }
  });
  return <div class="cursors" ref={ref} aria-hidden="true" />;
}

// A button component is a link on the published page; here a click on it
// is a click into its words.
addEventListener("click", (e) => {
  if ((e.target as HTMLElement).closest?.(".desk .page .c-button-link")) e.preventDefault();
}, true);

// Outside the text: undo undoes this tab's last edit, and cut and paste
// move the selected block.
addEventListener("keydown", (e) => {
  const editor = window.editor;
  const t = e.target as HTMLElement;
  if (!editor || !(e.ctrlKey || e.metaKey) || t.closest?.(".ProseMirror, .cm-editor, input, textarea, select")) return;
  const k = e.key.toLowerCase();
  const p = editor.page;
  if (k === "z" && !e.shiftKey) p.undo.undo();
  else if (k === "y" || (k === "z" && e.shiftKey)) p.undo.redo();
  else if (k === "x" && editor.selected && p.node(editor.selected) && !HIDDEN.has(p.type(editor.selected) ?? "")) {
    p.cut(editor.selected);
    editor.select(undefined);
  } else if (k === "v" && p.clipboard) {
    const at = editor.selected && p.node(editor.selected) ? editor.selected : undefined;
    const id = at ? p.paste(p.parent(at), p.index(at) + 1) : p.paste(undefined, editor.nodes.length);
    if (id) editor.select(id);
  } else return;
  e.preventDefault();
});

const STATES = { saved: "saved", unsaved: "not saved", saving: "saving…", failed: "not saved" } as const;

async function main() {
  await ready();
  const q = new URLSearchParams(location.search);
  const path = filePath();
  if (path) {
    const file = await FileSession.open(new SiteHost(path), {
      transport: q.get("transport") === "chaos" ? "chaos" : "channel",
      seed: Number(q.get("seed") ?? 1),
    });
    window.editor = file.editor;
    window.editorFile = file;
    addEventListener("hashchange", () => location.reload());
    const root = document.getElementById("app")!;
    root.replaceChildren();
    render(<App editor={file.editor} file={file} />, root);
    document.documentElement.dataset.ready = "1";
    return;
  }
  const editor = await Editor.open(pageId(), {
    transport: q.get("transport") === "chaos" ? "chaos" : "channel",
    seed: Number(q.get("seed") ?? 1),
    fixture: q.get("fixture") ?? undefined,
  });
  window.editor = editor;
  addEventListener("hashchange", () => location.reload());
  const root = document.getElementById("app")!;
  root.replaceChildren();
  render(<App editor={editor} />, root);
  document.documentElement.dataset.ready = "1";
}

// On its own page the editor starts itself; imported, it waits for mountEditor().
if (document.getElementById("app")?.hasAttribute("data-editor"))
  main().catch((e) => {
    document.getElementById("app")!.textContent = `The editor failed to start: ${e?.message ?? e}`;
    throw e;
  });
